import React, {useState} from 'react';
import PropTypes from 'prop-types';
import Row from 'react-bootstrap/Row';
import {css} from 'aphrodite';

import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faChevronCircleUp, faChevronCircleDown, faChevronUp, faChevronDown} from '@fortawesome/fontawesome-pro-regular';

import Markdown from '../Markdown';

const HeadingRenderer = ({level, className, id = undefined}) => ({node, children, ...props}) => {
  const Tag = `h${level}`;
  return (
    <Tag className={className} id={id} {...props}>
      {children}
    </Tag>
  );
};

const BoxedIsi = ({settings, styleSheet, source, topRightLink, fixed, expandable, enabled, toggleExpandBox, isExpanded}) => {
  const [iconHeight, setIconHeight] = useState(null);

  const getChildrenHeight = (node) => {
    if (node && iconHeight === null) {
      setIconHeight(node.offsetHeight);
    }
  }

  const linkRenderer = ({href, children}) => {
    if (settings.content.linkNewTab) {
      return (<a
        rel={'noopener noreferrer'}
        href={href}
        target={'_blank'}
        className={css(styleSheet.isiContentLink)}
      >
        {children}
      </a>);
    }

    return (<a
      href={href}
      className={css(styleSheet.isiContentLink)}
    >
      {children}
    </a>);
  };

  const boldRenderer = ({children}) => {
    return (<span className={css(styleSheet.isiContentBold)}>
      {children}
    </span>);
  };

  const headingComponents = Object.fromEntries(
    Array.from({length: 6}, (_, i) => [`h${i + 1}`, HeadingRenderer({level: i + 1, className: css(styleSheet.boxedIsiSpecialRenderer)})])
  );

  // vertically align two inline-block elements with one of them having dynamic height
  const topRightLinkHeight = iconHeight ? `${iconHeight}px` : '0';

  const topRightLinkElements = [];
  if (expandable) {
    const expandSymbol = settings.boxedIsi.expandToggleIconCircle ? faChevronCircleUp : faChevronUp;
    const collapseSymbol = settings.boxedIsi.expandToggleIconCircle ? faChevronCircleDown : faChevronDown;

    topRightLinkElements.push(
      <a
        ref={(node) => getChildrenHeight(node)}
        key={'expandLink'}
        data-testid="toggle-expanded-icon"
        style={{display: 'inline-block', lineHeight: topRightLinkHeight}}
        onClick={toggleExpandBox}>
        <FontAwesomeIcon
          className={css(styleSheet.topRightExpandToggle)}
          size={settings.boxedIsi.expandToggleIconSize || '2x'}
          icon={isExpanded ? collapseSymbol : expandSymbol}
        />
      </a>
    );
  }

  if (topRightLink) {
    topRightLinkElements.push(
      <a key={'topLink'} style={{display: 'inline-block', lineHeight: topRightLinkHeight}} href={topRightLink.href}>
        <span className={css(styleSheet.topRightLink)}>
          {topRightLink.name}
        </span>
      </a>
    );
  }

  if (!enabled) {
    return null;
  }

  if (fixed) {
    return (
      <Row
        data-testid="boxed-isi"
        className={isExpanded ? css(styleSheet.boxedIsi, styleSheet.fixedIsi, styleSheet.isiExpanded) : css(styleSheet.boxedIsi, styleSheet.fixedIsi, styleSheet.isiShrunk)}>
        <div className="container-custom">
          <div style={{float: 'right'}}>
            <span>
              {topRightLinkElements}
            </span>
          </div>
          <Markdown components={{a: linkRenderer, strong: boldRenderer, ...headingComponents}}>
            {source}
          </Markdown>
        </div>
      </Row>
    )
  }

  return (
    <Row data-testid="boxed-isi" className={isExpanded ? css(styleSheet.boxedIsi, styleSheet.isiExpanded) : css(styleSheet.boxedIsi, styleSheet.isiShrunk)}>
      {topRightLinkElements}
      <Markdown components={{a: linkRenderer, strong: boldRenderer, ...headingComponents}}>
        {source}
      </Markdown>
    </Row>
  );
}

BoxedIsi.protoTypes = {
  settings: PropTypes.object.isRequired,
  styleSheet: PropTypes.object.isRequired,
  source: PropTypes.any.isRequired,
  topRightLink: PropTypes.object.isRequired,
  fixed: PropTypes.object.isRequired,
  expandable: PropTypes.object.isRequired,
  enabled: PropTypes.bool.isRequired,
  toggleExpandBox: PropTypes.func.isRequired,
};

export default BoxedIsi;
